import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useAgentForgeStore } from '../../stores';
import type { MentionEditorHandle } from './MentionEditor.types';

// Per-session draft: restore on mount (ThreadContainer remounts the composer
// per session), autosave debounced while typing, clear on send. The
// serialized form is saved so mention chips survive a reload.
export const useSessionDraft = (
  editorRef: RefObject<MentionEditorHandle | null>
) => {
  const sessionIdAtMount = useRef(
    useAgentForgeStore.getState().activeSessionId
  );
  const initialDraft = useRef(
    sessionIdAtMount.current
      ? (useAgentForgeStore.getState().draftBySession[
          sessionIdAtMount.current
        ] ?? '')
      : ''
  );
  const setSessionDraft = useAgentForgeStore((s) => s.setSessionDraft);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleSave = useCallback(() => {
    const sid = sessionIdAtMount.current;
    if (!sid) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setSessionDraft(sid, editorRef.current?.getSerialized() ?? '');
    }, 400);
  }, [editorRef, setSessionDraft]);

  const clearDraft = useCallback(() => {
    const sid = sessionIdAtMount.current;
    if (!sid) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setSessionDraft(sid, '');
  }, [setSessionDraft]);

  // Flush the pending save on unmount so a fast session switch keeps the
  // last keystrokes.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const sid = sessionIdAtMount.current;
      if (sid && editorRef.current) {
        setSessionDraft(sid, editorRef.current.getSerialized());
      }
    },
    [editorRef, setSessionDraft]
  );

  return { initialDraft: initialDraft.current, scheduleSave, clearDraft };
};
